// File: src/pages/SavedProfilesPage.jsx
// Description: Modern, mobile-friendly saved profiles page with enhanced UX and visual appeal.

import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton,
  Grid,
  Tooltip,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  Skeleton,
  Badge,
  Fade,
  Zoom,
  TextField,
  MenuItem,
  LinearProgress,
  Snackbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { BASE_URL, AGENTIC_URL, AI_SERVER_EMAIL, AI_SERVER_PASSWORD } from '../config';
import { createAuthenticatedAPI } from '../utils/apiClient';

// Icons
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';

const SavedProfilesPage = () => {
  const { user, logout } = useContext(AuthContext);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Create authenticated API client
  const api = createAuthenticatedAPI({ user, logout, navigate });

  // Helper function to extract slug from a profile link (robust + legacy)
  const extractSlug = (profileLink) => {
    if (!profileLink) return null;
    try {
      const u = new URL(profileLink);
      const segs = u.pathname.split('/').filter(Boolean);
      const last = segs[segs.length - 1] || null;
      return last ? last.toLowerCase().replace(/\./g, '-') : null;
    } catch (e) {
      const parts = String(profileLink).split('/u/');
      const raw = parts.length > 1 ? parts[1] : null;
      return raw ? raw.toLowerCase().replace(/\./g, '-') : null;
    }
  };

  // JWT helpers to detect expired sessions locally
  const decodeJwt = (token) => {
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
      try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
    }
  };

  const isTokenExpired = (token) => {
    const data = decodeJwt(token);
    if (!data || !data.exp) return false; // if unknown, let server decide
    const now = Math.floor(Date.now() / 1000);
    return data.exp <= now;
  };

  // Fetch saved profiles list, then for each entry, fetch full profile details
  useEffect(() => {
    const fetchSavedProfiles = async () => {
      try {
        if (!user || !user.token) {
          throw new Error('User not authenticated');
        }

        console.log('Fetching saved profiles from:', `${BASE_URL}/users/saved-profiles`);
        console.log('User token:', user.token ? 'Present' : 'Missing');
        
        const data = await api.get('/users/saved-profiles');
        console.log('Saved profiles data:', data);
        const savedEntries = data.saved_profiles;

        if (!savedEntries || savedEntries.length === 0) {
          console.log('No saved profiles found');
          setSavedProfiles([]);
          return;
        }

        console.log('Processing', savedEntries.length, 'saved profile entries');
        
        const profilePromises = savedEntries.map(async (entry, index) => {
          const slug = extractSlug(entry.profileLink);
          if (!slug) {
            console.warn('Could not extract slug from profile link:', entry.profileLink);
            return null;
          }
          
          try {
            console.log(`Fetching profile ${index + 1}/${savedEntries.length} for slug:`, slug);
            const res = await fetch(`${BASE_URL}/users/public-profile/${slug}`, {
              headers: { 'Content-Type': 'application/json' }
            });
            
            if (!res.ok) {
              console.error(`Failed to fetch profile for slug ${slug}:`, res.status, res.statusText);
              return null;
            }
            
            const profileDetails = await res.json();
            console.log(`Profile ${index + 1} fetched successfully:`, profileDetails.name);
            return { ...profileDetails, savedTimestamp: entry.timestamp };
          } catch (err) {
            console.error(`Error fetching details for slug ${slug}:`, err);
            return null;
          }
        });
        
        const profiles = await Promise.all(profilePromises);
        const validProfiles = profiles.filter((p) => p !== null);
        console.log('Successfully fetched', validProfiles.length, 'profiles out of', savedEntries.length, 'entries');
        setSavedProfiles(validProfiles);
      } catch (err) {
        console.error('Error in fetchSavedProfiles:', err);
        
        // Check if it's a network/CORS error
        if (err.message.includes('Failed to fetch') || err.message.includes('CORS')) {
          setError('Unable to connect to the server. Please check if the backend is running and try again.');
        } else if (err.message.includes('500')) {
          setError('Server error occurred. Please try again later or contact support.');
        } else {
          setError(err.message || 'An unexpected error occurred while fetching saved profiles.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSavedProfiles();
  }, [user]);

  // Handler to view a full profile in a new tab
  const handleViewProfile = (public_slug) => {
    const profileUrl = `${window.location.origin}/u/${public_slug}`;
    window.open(profileUrl, '_blank');
  };

  // Handler to view networking opportunities
  const handleViewOpportunities = async (savedProfile) => {
    if (!user) return;
    setOpportunitiesLoading(true);
    
    const payload = {
      email: AI_SERVER_EMAIL,
      password: AI_SERVER_PASSWORD,
      agentic_name: "Profile Analysis and Collaboration Insight Agent",
      user_input: `Profile 1: ${user.job_title || user.jobTitle || ''} ${user.name || ''} ${user.bio || ''}\nProfile 2: ${savedProfile.job_title || ''} ${savedProfile.name || ''} ${savedProfile.bio || ''}`
    };

    try {
      console.log('Sending AI request with payload:', payload);
      const res = await fetch(AGENTIC_URL, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      console.log('AI server response status:', res.status);
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('AI server error response:', errorData);
        throw new Error(errorData.detail || `AI server error: ${res.status}`);
      }
      
      const data = await res.json();
      console.log('AI server success response:', data);
      const result = data.result;
      
      navigate("/networking-opportunities", { state: { result } });
    } catch (error) {
      console.error("API Error:", error);
      alert("Failed to retrieve networking opportunities: " + error.message);
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  // Copy profile link to clipboard
  const handleCopyLink = (public_slug) => {
    const link = `${window.location.origin}/u/${public_slug}`;
    navigator.clipboard.writeText(link).catch(() => {});
  };

  // Search and sort controls
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent | oldest | name

  const visibleProfiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...savedProfiles];
    if (q) {
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.company || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    }
    if (sortBy === 'recent') {
      list.sort((a, b) => new Date(b.savedTimestamp) - new Date(a.savedTimestamp));
    } else if (sortBy === 'oldest') {
      list.sort((a, b) => new Date(a.savedTimestamp) - new Date(b.savedTimestamp));
    } else if (sortBy === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return list;
  }, [savedProfiles, query, sortBy]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  // Loading skeleton
  const ProfileSkeleton = () => (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={3} sm={2}>
            <Skeleton variant="circular" width={80} height={80} />
          </Grid>
          <Grid item xs={9} sm={6}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Profile card component
  const ProfileCard = ({ profile, index }) => (
    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={3} alignItems="center">
            {/* Profile Picture */}
            <Grid item xs={3} sm={2}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <BookmarkIcon 
                    sx={{ 
                      fontSize: 16, 
                      color: theme.palette.primary.main,
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      p: 0.5
                    }} 
                  />
                }
              >
                <Avatar
                  src={profile.profile_picture}
                  alt={profile.name}
                  sx={{ 
                    width: { xs: 60, md: 80 }, 
                    height: { xs: 60, md: 80 },
                    border: `3px solid ${theme.palette.primary[100]}`,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}
                >
                  {profile.name ? profile.name.charAt(0).toUpperCase() : '?'}
                </Avatar>
              </Badge>
            </Grid>

            {/* Profile Details */}
            <Grid item xs={9} sm={6}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  mb: 0.5,
                  color: theme.palette.text.primary
                }}
              >
                {profile.name}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  mb: 1
                }}
              >
                {profile.job_title || 'Professional'}
                {profile.company && (
                  <span style={{ color: theme.palette.text.secondary, fontWeight: 400 }}>
                    {' '}@ {profile.company}
                  </span>
                )}
              </Typography>

              {/* Location */}
              {profile.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOnIcon 
                    sx={{ 
                      fontSize: 16, 
                      color: theme.palette.text.secondary,
                      mr: 0.5 
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary">
                    {profile.location}
                  </Typography>
                </Box>
              )}

              {/* Bio preview */}
              {profile.bio && (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {profile.bio}
                </Typography>
              )}

              {/* Contact chips */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {profile.phone_number && (
                  <Chip
                    icon={<PhoneIcon />}
                    label="Phone"
                    size="small"
                    variant="outlined"
                    sx={{ 
                      backgroundColor: theme.palette.primary[50],
                      borderColor: theme.palette.primary[200],
                      color: theme.palette.primary[700]
                    }}
                  />
                )}
                {profile.email && (
                  <Chip
                    icon={<EmailIcon />}
                    label="Email"
                    size="small"
                    variant="outlined"
                    sx={{ 
                      backgroundColor: theme.palette.secondary[50],
                      borderColor: theme.palette.secondary[200],
                      color: theme.palette.secondary[700]
                    }}
                  />
                )}
                {profile.whatsapp && (
                  <Chip
                    icon={<WhatsAppIcon />}
                    label="WhatsApp"
                    size="small"
                    variant="outlined"
                    sx={{ 
                      backgroundColor: '#25D36620',
                      borderColor: '#25D366',
                      color: '#25D366'
                    }}
                  />
                )}
              </Stack>
            </Grid>

            {/* Contact & Actions */}
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                display: 'flex', 
                gap: 1.5, 
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mb: 2,
                flexWrap: 'wrap'
              }}>
                {profile.phone_number && (
                  <Tooltip title="Call Now">
                    <IconButton 
                      href={`tel:${profile.phone_number}`} 
                      size="medium"
                      sx={{
                        backgroundColor: theme.palette.primary[100],
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary[200],
                        }
                      }}
                    >
                      <PhoneIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {profile.email && (
                  <Tooltip title="Email Me">
                    <IconButton 
                      href={`mailto:${profile.email}`} 
                      size="medium"
                      sx={{
                        backgroundColor: theme.palette.secondary[100],
                        color: theme.palette.secondary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.secondary[200],
                        }
                      }}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {profile.whatsapp && (
                  <Tooltip title="Chat on WhatsApp">
                    <IconButton
                      href={`https://wa.me/${profile.whatsapp}`}
                      target="_blank"
                      size="medium"
                      sx={{
                        backgroundColor: '#25D36620',
                        color: '#25D366',
                        '&:hover': {
                          backgroundColor: '#25D36630',
                        }
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Saved timestamp */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mb: 2
              }}>
                <AccessTimeIcon 
                  sx={{ 
                    fontSize: 16, 
                    color: theme.palette.text.secondary,
                    mr: 0.5 
                  }} 
                />
                <Typography variant="caption" color="text.secondary">
                  Saved {formatTimestamp(profile.savedTimestamp)}
                </Typography>
              </Box>

              {/* Quick actions */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', sm: 'flex-end' }, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCopyLink(profile.public_slug)}
                  sx={{ borderRadius: 2, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                  Copy Link
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleViewProfile(profile.public_slug)}
                  startIcon={<VisibilityIcon />}
                  sx={{ borderRadius: 2, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                  View Profile
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`
          }}>
            <Button
              variant="gradient"
              size="medium"
              onClick={() => handleViewOpportunities(profile)}
              startIcon={<BusinessCenterIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: '0.95rem',
                fontWeight: 600
              }}
              disabled={opportunitiesLoading}
            >
              View Opportunities
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Zoom>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            sx={{ 
              mr: 2,
              backgroundColor: theme.palette.primary[100],
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary[200],
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              My Saved Profiles
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Access your saved professional connections and explore collaboration opportunities
            </Typography>
          </Box>
        </Box>

        {/* Stats */}
        {!loading && !error && savedProfiles.length > 0 && (
          <Card sx={{ 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            border: `1px solid ${theme.palette.divider}`
          }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {savedProfiles.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Saved Profiles
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                      {savedProfiles.filter(p => p.phone_number || p.email || p.whatsapp).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contactable
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                      {savedProfiles.filter(p => p.company).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      With Company Info
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search by name, company, or location"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Sort by"
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="recent">Most Recent</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="name">Name (A–Z)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Box>
          {[...Array(3)].map((_, index) => (
            <ProfileSkeleton key={index} />
          ))}
        </Box>
      ) : error ? (
        <Card sx={{ 
          mb: 3, 
          borderRadius: 3,
          border: `2px solid ${theme.palette.error.main}`,
          background: theme.palette.error[50]
        }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-message': { fontSize: '1rem' }
              }}
            >
              {error}
            </Alert>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              This could be due to:
            </Typography>
            
            <Box sx={{ textAlign: 'left', mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Backend server is not running
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Database connection issues
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Network connectivity problems
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  borderWidth: 2
                }}
              >
                Retry
              </Button>
              <Button
                variant="gradient"
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Back to Dashboard
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : visibleProfiles.length === 0 ? (
        <Card sx={{ 
          borderRadius: 3, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ py: 6 }}>
            <PersonIcon sx={{ fontSize: 80, color: theme.palette.text.secondary, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              No Saved Profiles Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start building your professional network by saving profiles that interest you.
            </Typography>
            <Button
              variant="gradient"
              size="large"
              onClick={() => navigate('/dashboard')}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Explore Profiles
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {visibleProfiles.map((profile, index) => (
            <ProfileCard key={`${profile.public_slug || index}-${index}`} profile={profile} index={index} />
          ))}
        </Box>
      )}

      {/* Back to Dashboard Button */}
      {!loading && !error && savedProfiles.length > 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/dashboard')}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              borderWidth: 2,
              fontSize: '1rem',
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-1px)',
              }
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      )}

      {/* Opportunities Loading Indicator */}
      {opportunitiesLoading && (
        <>
          <LinearProgress 
            color="primary"
            sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 1300 }} 
          />
          <Snackbar
            open
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            message="Please wait while analysing possible collaboration opportunities…"
            sx={{ mt: 6 }}
          />
        </>
      )}
    </Container>
  );
};

export default SavedProfilesPage;
